import React from 'react';
import { useHistory, useParams } from 'react-router';
import { AuthorizedSignerDetailFragment } from '../../../graphql/fragments/authorizedSignerDetail.generated';
import { useAuthorizedSignerQuery } from '../../../graphql/queries/authorizedSigner.generated';
import { EditItem, EditSection } from '../../../components/Layout';
import i18n from '@/utils/i18n';
import ClassicLoadingSpinner from '../../../components/LoadingSpinner/ClassicLoadingSpinner';
import { LayoutStoreInstance } from '../../../LayoutStore';
import { Link } from 'react-router-dom';
import { useUpdateAuthorizedSignerMutation } from '../../../graphql/mutations/updateAuthorizedSigner.generated';
import { useDeleteAuthorizedSignerMutation } from '../../../graphql/mutations/deleteAuthorizedSigner.generated';
import { Button } from 'reactstrap';
import { FaDownload, FaTrash } from 'react-icons/fa';
import OrderFulfillmentsTableWithFilter from '../components/OrderFulfillmentsTableWithFilter';
import DownloadAuthorizedSignerPdf from './AuthorizedSignerPdf';
import ItemLocationEditTable from './ItemLocationsEditTable';
import { EventAccount, EventReduced } from '../../../graphql/generatedTypes';
import { ContentSection } from '../../../components/Layout/ContentSection';
import { Routes } from '@/utils/routes';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

type ItemLocationReduced = {
  can_be_deleted?: boolean;
  event?: EventReduced;
  event_account?: EventAccount;
  event_account_id?: string;
  event_id?: string;
  id: string;
  name: string;
  type?: string;
};

type IdName = {
  id: string;
  name: string;
};

export const AuthorizedSignerDetailPage = () => {
  const { authorizedSignerId } = useParams<{ authorizedSignerId: string }>();
  const history = useHistory();
  const [authorizedSigner, setAuthorizedSigner] =
    React.useState<AuthorizedSignerDetailFragment>();

  useDocumentTitle('PageTitles:authorizedSigner', authorizedSigner?.name);

  const [name, setName] = React.useState<string>('');
  const [allowedLocations, setAllowedLocations] = React.useState<
    ItemLocationReduced[] | undefined
  >([]);

  const { loading, error } = useAuthorizedSignerQuery({
    variables: {
      id: authorizedSignerId,
    },
    onCompleted: (data) => {
      setAuthorizedSigner(data.authorizedSigner);
      setName(data.authorizedSigner.name);
      if (data.authorizedSigner.can_use_all_item_locations) {
        setAllowedLocations(
          (data.authorizedSigner.event_account.item_locations?.map((loc) => ({
            id: loc.id,
            name: loc.name,
          })) as IdName[]) ?? [],
        );
      } else {
        setAllowedLocations(
          (data.authorizedSigner.allowed_item_locations?.map((loc) => ({
            id: loc.id,
            name: loc.name,
          })) as IdName[]) ?? [],
        );
      }
    },
  });
  const handleCheckboxChange = (locationId: string) => {
    const index = allowedLocations?.findIndex((l) => l.id === locationId);
    if (index === -1) {
      const location = authorizedSigner?.event_account?.item_locations?.find(
        (l) => l.id === locationId,
      );
      if (location != null) {
        setAllowedLocations([
          ...(allowedLocations || [] || []),
          location as IdName,
        ]);
      }
    } else {
      setAllowedLocations(allowedLocations?.filter((l) => l.id !== locationId));
    }
  };

  const [updateAuthorizedSigner, { loading: updating }] =
    useUpdateAuthorizedSignerMutation({
      variables: {
        authorized_signer_id: authorizedSignerId,
        name: name,
        allowed_item_location_ids: allowedLocations?.map((l) => l.id),
      },
    });

  const [deleteAuthorizedSigner, { loading: deleting }] =
    useDeleteAuthorizedSignerMutation({
      variables: {
        authorized_signer_id: authorizedSignerId,
      },
      onCompleted: () => {
        LayoutStoreInstance.setSuccess(
          i18n.t('AuthorizedSigner:authorizedSignerDeleted'),
        );

        if (authorizedSigner?.event_account_id) {
          history.push(
            Routes.eventAccountDetail(authorizedSigner?.event_account_id),
          );
        }
      },
      onError: (error) => {
        LayoutStoreInstance.setError(i18n.t('AuthorizedSigner:errorOnDelete'));
        console.error(error);
      },
    });
  if (error) {
    LayoutStoreInstance.setError(i18n.t('AuthorizedSigner:errorOnLoad'));
    console.error(error);
  }

  return (
    <EditSection onSave={updateAuthorizedSigner} isLoading={updating}>
      <ContentSection
        title={i18n.t('AuthorizedSigner:authorizedSigner')}
        collapsable={false}
      >
        {loading && <ClassicLoadingSpinner />}
        {authorizedSigner != null && (
          <div>
            <EditItem title={i18n.t('Common:generalInfo')}>
              <div className="text-end">
                <Button
                  className="mb-2 me-2 mt-2"
                  color="secondary"
                  onClick={() =>
                    DownloadAuthorizedSignerPdf(
                      [authorizedSigner],
                      authorizedSigner.name,
                    )
                  }
                >
                  <FaDownload />
                  {' ' + i18n.t('Common:download')}
                </Button>
                <Button
                  className="mb-2 mt-2"
                  color="danger"
                  disabled={deleting}
                  onClick={() => deleteAuthorizedSigner()}
                >
                  <FaTrash /> {i18n.t('Common:delete')}
                </Button>
              </div>
              <div className="input-group mb-3">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('Common:name')}
                </span>
                <input
                  type="text"
                  className="form-control"
                  onChange={(e) => setName(e.target.value)}
                  value={name}
                />
              </div>
              <div className="input-group mb-3">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('AuthorizedSigner:eventAccount')}
                </span>
                <Link
                  className="form-control"
                  to={'/event-account/' + authorizedSigner.event_account_id}
                >
                  {authorizedSigner.event_account_name}
                </Link>
              </div>
            </EditItem>
            <EditItem title={i18n.t('AuthorizedSigner:allowedToSingFor')}>
              <ItemLocationEditTable
                itemLocations={
                  authorizedSigner?.event_account?.item_locations ?? []
                }
                allowedLocationIds={allowedLocations?.map((l) => l.id) || []}
                onChange={handleCheckboxChange}
                toggleAll={() => {
                  if (
                    allowedLocations?.length ===
                    authorizedSigner?.event_account.item_locations?.length
                  ) {
                    setAllowedLocations([]);
                  } else {
                    setAllowedLocations(
                      (authorizedSigner?.event_account.item_locations ??
                        []) as IdName[],
                    );
                  }
                }}
              />
            </EditItem>
            <EditItem title={i18n.t('AuthorizedSigner:orderFulfillments')}>
              <OrderFulfillmentsTableWithFilter
                filter={{ authorized_signer_id: authorizedSignerId }}
                authorizedSigners={[
                  { id: authorizedSigner.id, name: authorizedSigner.name },
                ]}
                events={[]}
                itemLocations={
                  authorizedSigner.event_account.item_locations?.map((i) => ({
                    id: i.id,
                    name: i.name,
                  })) ?? []
                }
                eventAccounts={[
                  {
                    id: authorizedSigner.event_account_id,
                    name: authorizedSigner.event_account_name,
                  },
                ]}
              />
            </EditItem>
          </div>
        )}
      </ContentSection>
    </EditSection>
  );
};
